/**
 * Small store combinators. Plain `svelte/store` only - no firebase, so they can be probed.
 */
import type { Readable, Subscriber, Unsubscriber } from "svelte/store";

/** Whatever this svelte's `Readable` calls its optional second `subscribe` argument. */
type Invalidate<T> = Parameters<Readable<T>["subscribe"]>[1];

/**
 * `source`, re-emitted only when `keyOf(value)` changes.
 *
 * The point is to sit between a chatty source (an `onSnapshot` document store, an auth store
 * that re-emits on every token refresh) and a consumer that treats every emission as "start
 * over" - `chainStores`, which tears down and recreates its inner store on each one. Squashing
 * the emissions that carry no new key means the expensive inner store is created once per
 * distinct key rather than once per snapshot.
 *
 * The memo lives in `subscribe`, i.e. one per subscription, never in the module. That is what
 * makes it safe for a subscriber to come and go: the *first* value a new subscriber sees is
 * always delivered, however many times some earlier subscriber saw the same key, so a consumer
 * that resubscribes after the last one dropped is handed the current value and rebuilds. A
 * module-level memo would swallow that first delivery and leave the consumer on its default
 * forever.
 */
export function distinctBy<T>(source: Readable<T>, keyOf: (value: T) => string): Readable<T> {
  return {
    subscribe(run: Subscriber<T>, invalidate?: Invalidate<T>): Unsubscriber {
      let lastKey: string | undefined = undefined;
      let delivered = false;
      return source.subscribe((value) => {
        const key = keyOf(value);
        if (delivered && key === lastKey) return;
        lastKey = key;
        delivered = true;
        run(value);
      }, invalidate);
    },
  };
}
